// services/onDemandService.js
// API methods for SOS / on-demand consultation requests.

const API_URL = 'https://odoo.core.daisies.mx/odoo-firebase-core/odoo-import';
const ACCESS_TOKEN = process.env.REACT_APP_ACCESS_TOKEN;
const ACCOUNT_ID = parseInt(process.env.REACT_APP_ACCOUNT_ID, 10);

/**
 * Build the standard request payload used by all Odoo endpoints.
 */
const buildPayload = (authToken, resModel, resId, resMethod, resParams) => ({
  params: {
    access_token: ACCESS_TOKEN,
    account_id: ACCOUNT_ID,
    auth_token: authToken,
    res_model: resModel,
    res_id: resId,
    res_method: resMethod,
    res_params: resParams
  }
});

/**
 * Generic POST helper.
 */
const post = async (payload) => {
  const response = await fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return response.json();
};

// ─────────────────────────────────────────────
// 1. Get available schedules for an on-demand request
// ─────────────────────────────────────────────
/**
 * Fetches available time slots for a given doctor/duration/date.
 *
 * @param {string}  authToken      – Firebase auth token
 * @param {number}  userId         – Odoo dais.user ID
 * @param {number}  professionalId – Doctor/professional ID
 * @param {number}  durationId     – Duration option ID
 * @param {string}  date           – "YYYY-MM-DD"
 * @returns {Promise<{success: boolean, slots: Array<{id: number, init: string}>}>}
 */
export const getAvailableSchedulesForRequest = async (
  authToken,
  userId,
  professionalId,
  durationId,
  date
) => {
  try {
    const payload = buildPayload(
      authToken,
      'dais.ondemand',
      1,
      'get_available_schedules_for_request',
      {
        proposal_by: 'user',
        user_id: userId,
        professional_id: professionalId,
        duration_id: durationId,
        date: date
      }
    );

    const data = await post(payload);

    if (data.result?.success || data.result?.data) {
      return {
        success: true,
        slots: data.result.data || []
      };
    }

    return { success: false, slots: [], error: data.result?.message || 'No slots found' };
  } catch (error) {
    console.error('[onDemandService] getAvailableSchedulesForRequest error:', error);
    return { success: false, slots: [], error: error.message };
  }
};

// ─────────────────────────────────────────────
// 2. Create an on-demand (SOS) consultation request
// ─────────────────────────────────────────────
/**
 * Creates the SOS request with selected schedule IDs.
 *
 * @param {string}  authToken      – Firebase auth token
 * @param {number}  userId         – Odoo dais.user ID
 * @param {number}  professionalId – Doctor/professional ID
 * @param {number}  durationId     – Duration option ID
 * @param {number[]} scheduleIds   – Array of selected schedule IDs (max 3)
 * @param {string}  description    – Consultation reason text
 * @param {number}  consultationReasonId – Reason category ID (0 for none)
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const createOnDemandRequest = async (
  authToken,
  userId,
  professionalId,
  durationId,
  scheduleIds,
  description,
  consultationReasonId = 0
) => {
  try {
    const payload = buildPayload(
      authToken,
      'dais.ondemand',
      1,
      'create_on_demand_request',
      {
        proposal_by: 'user',
        user_id: userId,
        professional_id: professionalId,
        duration_id: durationId,
        consultation_reason_id: consultationReasonId,
        dates: scheduleIds,
        desc: description
      }
    );

    const data = await post(payload);

    if (data.result?.success) {
      return {
        success: true,
        message: data.result.message || 'Solicitud creada correctamente'
      };
    }

    return {
      success: false,
      message: data.result?.message || 'Error al crear la solicitud'
    };
  } catch (error) {
    console.error('[onDemandService] createOnDemandRequest error:', error);
    return { success: false, message: error.message };
  }
};
